package com.milta.feature.route.presentation

import com.milta.core.network.PagedResult
import com.milta.feature.route.domain.entity.RouteHistoryItem
import com.milta.feature.route.domain.entity.RouteProofsResult
import com.milta.feature.team.domain.entity.Driver

/**
 * Which date filter is active on Screen A (MIL-52). [LastEightDays] is the default and omits
 * `from`/`to` entirely (server defaults to the rolling last 8 days); [SingleDate]/[Range] both send
 * explicit `yyyy-MM-dd` bounds, client-clamped to `[today-7, today]` by the date pickers' own
 * `min`/`max` before ever reaching the API (the backend also rejects an out-of-range `from` with a
 * 400 `error.invalid_date` — belt-and-suspenders, see [WebHistoriqueViewModel.mapHistoryError]).
 */
enum class HistoriquePeriodMode { LastEightDays, SingleDate, Range }

/**
 * State for MIL-52 "Historique des preuves de livraison" — a single state holder for BOTH screens
 * of the flow (Screen A "Historique des tournées" list, Screen B "Preuves de la tournée" drill-down
 * grid), same consolidation precedent as [com.milta.feature.settings.presentation.WebSettingsState]/
 * [com.milta.feature.team.presentation.WebTeamState]: the ticket explicitly requires Screen A's
 * filters ([periodMode]/[singleDate]/[rangeFrom]/[rangeTo]/[selectedDriverId]) to survive
 * navigating into and back out of Screen B, which is only possible if both screens share one
 * lifecycle/state holder rather than each owning (and losing) their own on navigation.
 * [selectedRouteId] is the sole switch between the two screens — null = Screen A, non-null = Screen B.
 */
data class WebHistoriqueState(
    // ── Screen A — "Historique des tournées" ──
    val periodMode: HistoriquePeriodMode = HistoriquePeriodMode.LastEightDays,
    /** `yyyy-MM-dd`, only meaningful when [periodMode] is [HistoriquePeriodMode.SingleDate]. */
    val singleDate: String? = null,
    /** `yyyy-MM-dd`, only meaningful when [periodMode] is [HistoriquePeriodMode.Range]. */
    val rangeFrom: String? = null,
    val rangeTo: String? = null,
    /** Null = "Tous les livreurs". */
    val selectedDriverId: String? = null,
    val drivers: List<Driver> = emptyList(),
    val historyPage: PagedResult<RouteHistoryItem>? = null,
    val historyPageNumber: Int = 1,
    val isLoadingHistory: Boolean = false,
    /** "invalid_period" | "server_error" | "offline". */
    val historyError: String? = null,

    // ── Screen B — "Preuves de la tournée" ──
    val selectedRouteId: String? = null,
    val proofsResult: RouteProofsResult? = null,
    val proofsPageNumber: Int = 1,
    val isLoadingProofs: Boolean = false,
    /** "route_not_found" | "forbidden" | "server_error" | "offline". */
    val proofsError: String? = null,
) {
    val isProofsScreen: Boolean get() = selectedRouteId != null
    val historyTotalPages: Int get() = historyPage?.totalPages ?: 0
    val proofsTotalPages: Int get() = proofsResult?.proofs?.totalPages ?: 0
}
